/**
 * `R3-UI0` · **EL ENCABEZADO DE COLUMNA ORDENABLE ▲▼ — MUESTRA Y AVISA, NO
 * ORDENA.**
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔⛔ LO PRIMERO, PORQUE ES LO QUE ESTA PRIMITIVA TIENE PROHIBIDO HACER
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R3.md:1993` (R3-F5 regla 3), textual: *«**El orden vale para toda la
 * lista, no solo para la página visible:** con 1.184 participantes y 10 filas por
 * página, ordenar por Nombre de la Z a la A deja en la primera página a los diez
 * últimos del alfabeto de todo el brazo.»*
 *
 * ⇒ **esta primitiva no ordena filas.** Recibe cuál es el orden activo, dibuja
 * las flechas y avisa el toque; el orden lo pide la pantalla al servidor y vuelve
 * la página 1 ya ordenada. Una primitiva que hiciera `items.sort()` ordenaría
 * **las diez filas que la página trajo** y se vería correcta en la pantalla y en
 * cualquier examen de una sola página: es el modo de falla que esa regla nombra
 * con un número concreto, y el único lugar donde se puede prohibir es acá, antes
 * de que exista el primer consumidor.
 *
 * ⚠️ Por eso este archivo **no tiene estado**. Ni la columna activa ni el sentido
 * viven acá: los tiene la pantalla, que es la que los manda al borde. Guardar el
 * sentido adentro del `<th>` haría que la flecha y la consulta pudieran decir
 * cosas distintas, y la flecha ganaría la discusión visualmente.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐ LAS DOS FLECHAS SIEMPRE, Y UNA SOLA RESALTADA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R3.md:1982`: *«en su encabezado se ve el indicador ▲▼»* — en TODA columna
 * ordenable, también en las que no están activas: es lo que dice que se puede
 * tocar. `:1992`: *«La columna activa muestra resaltada la flecha del sentido: ▲
 * en el primer toque, ▼ en el segundo.»*
 *
 * ⇒ se dibujan **las dos flechas siempre** y se resalta **una** con
 * `--activa`. ⛔ La alternativa —dibujar sólo la flecha del sentido activo y nada
 * en las inactivas— cambia el ancho del encabezado al ordenar y **no dice qué
 * columnas se pueden ordenar**, que es la mitad del trabajo del indicador.
 *
 * ⚠️ **El resaltado es una CLASE, no un carácter distinto.** Las dos flechas son
 * siempre `▲` (U+25B2) y `▼` (U+25BC); lo que cambia es la regla de
 * `estilos.css` §«Patrones de R3». Distinguirlas por carácter —`▵`/`▴`— ataría la
 * señal a la tipografía instalada, y `happy-dom` no puede afirmar ni un color ni
 * un peso: lo que el examen mide es **la clase**, que es la causa.
 *
 * ⛔ Y va además `aria-sort` en el `<th>` —`ascending`, `descending`, `none`—,
 * que es la mitad de la señal que no es visual. Una flecha resaltada no existe
 * para un lector de pantalla.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ NO ES LA ÚNICA DEFINICIÓN DE LOS ▲▼ EN EL ÁRBOL, Y ESO VA DECLARADO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `[medido 25/9/2026]` **Existe una segunda implementación, PREEXISTENTE y
 * divergente**, en `apps/management-ui/src/banco-de-contenido/tabla.tsx:502-515`:
 * su propio `<th aria-sort=…>` con `<button className="banco__orden">` y
 * `{direccion === "asc" ? "▲" : direccion === "desc" ? "▼" : "↕"}` (`:512`).
 * Difiere en **la forma de la señal**: muestra **UNA** flecha, o `↕` cuando no
 * ordena, mientras `PRD_R3.md:1982` pide *«en su encabezado se ve el indicador
 * ▲▼»* en toda columna ordenable.
 *
 * ⛔ **No se unifica acá, y no es una omisión.** El Banco de contenido es de
 * `R1-F3`: está **fuera del territorio de `ui-marco`** y fuera de toda autoridad de
 * R3. `R3-UI0` no copió nada —esta primitiva nació antes de su primer consumidor—,
 * pero el árbol tiene **dos** definiciones y la segunda es exactamente la especie
 * que el `rationale_hint` de B6 dice que se paga *«en el primer ajuste»*.
 *
 * ⛔⛔ **Y tiene una consecuencia concreta ya escrita, que es la razón de esta
 * nota:** el control positivo de `pruebas/aw47-declaracion-del-banco.test.tsx:188`
 * (§5.12) cuelga de esa implementación
 * (`querySelectorAll("th button").length > 0`). El día que alguien migre el Banco a
 * esta primitiva —que es el movimiento natural— **ese control positivo se apaga o
 * cambia de significado**, y con él el criterio `R1-F3-28` que la `PARADA` de
 * `paginado.tsx` docblock ③ acaba de proteger. **Unificar es alcance de `R1-F3`, y
 * cuando se haga hay que rever `aw47` §5.12 en la misma etapa.** Dueño: `R1-F3` / TL.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ QUÉ SENTIDO PIDE CADA TOQUE, Y POR QUÉ LO DECIDE LA PRIMITIVA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R3.md:1991`: *«Cada toque siguiente sobre la misma columna vuelve a
 * invertir el orden. Tocar otra columna ordena por esa, con su primer toque.»*
 *
 * ⇒ la regla es de DOS renglones y la aplica esta unidad:
 *   · la columna ya activa ⇒ el sentido contrario al que está;
 *   · cualquier otra ⇒ `"asc"`, que es «su primer toque».
 *
 * ⚠️ Dejársela a la pantalla es lo que produce las dos versiones del mismo
 * `if`: `R3-F5` la quiere para siete columnas y el día que otra tabla sume la
 * octava, la que se escriba de nuevo va a arrancar en `"desc"` y nadie lo va a
 * ver. La pantalla recibe el sentido ya decidido y lo único que hace es pedirlo.
 */

import type { JSX } from "react"

/** El sentido del orden, en los dos valores que viajan al borde. */
export type SentidoDeOrden = "asc" | "desc"

/** Qué columna está ordenando la lista, y en qué sentido. */
export interface OrdenActivo<C extends string = string> {
  readonly columna: C
  readonly sentido: SentidoDeOrden
}

/**
 * El sentido que pide el próximo toque sobre `columna`.
 *
 * Se exporta porque es la regla, y un examen la puede afirmar sin montar un
 * `<table>`: es la parte de esta unidad que no depende del DOM.
 */
export function proximoSentido<C extends string>(
  orden: OrdenActivo<C> | null,
  columna: C,
): SentidoDeOrden {
  if (orden === null || orden.columna !== columna) return "asc"
  return orden.sentido === "asc" ? "desc" : "asc"
}

export function EncabezadoOrdenable<C extends string>(props: {
  /** El identificador de la columna, el que viaja al borde. */
  readonly columna: C
  /** El rótulo que se ve. `PRD_R3.md:1971-1977` los fija uno por uno. */
  readonly rotulo: string
  /** El orden activo de la lista, o `null` si ninguna columna lo está. */
  readonly orden: OrdenActivo<C> | null
  /**
   * El toque, con el sentido **ya decidido** por `proximoSentido`.
   *
   * ⛔ Recibe los dos valores y no sólo la columna: si el manejador tuviera que
   * deducir el sentido, la regla de `:1991` volvería a estar escrita en cada
   * pantalla, que es lo que este archivo existe para evitar.
   */
  readonly onOrdenar: (columna: C, sentido: SentidoDeOrden) => void
}): JSX.Element {
  const { columna, rotulo, orden, onOrdenar } = props
  const activa = orden !== null && orden.columna === columna
  const sentido = activa && orden !== null ? orden.sentido : null

  /*
   * ⛔⛔ **ESTAS DOS CONSTANTES EXISTEN POR EL CENSO DE CLASES, Y ES UN HECHO
   * MEDIDO, no una preferencia de estilo.** `[25/9/2026]`
   *
   * `pruebas/r11-clases-sin-regla.test.ts` cosecha **TODOS los literales de cadena
   * que aparecen dentro de un `className={…}`** y le exige una regla a cada uno.
   * Con la comparación escrita adentro del atributo —`className={sentido === "asc"
   * ? … : …}`— el censo tomaba **`asc` y `desc` como si fueran clases** y el caso
   * se ponía rojo pidiendo `.asc` y `.desc` en la hoja:
   *
   *     + "asc · encabezado-ordenable.tsx",
   *     + "desc · encabezado-ordenable.tsx",
   *
   * ⇒ la comparación sale del atributo y queda acá. La alternativa era agregar
   * `asc` y `desc` a `SIN_REGLA_A_PROPOSITO` —el mecanismo existe y tiene el
   * precedente de `guardrails`—, y es peor: mete dos palabras genéricas en la lista
   * de excepciones para siempre, cuando lo que sobra es la comparación en el
   * atributo. **Se saca la causa, no se declara la excepción.**
   */
  const resaltaAscendente = sentido === "asc"
  const resaltaDescendente = sentido === "desc"

  return (
    <th
      scope="col"
      className="encabezado-ordenable"
      aria-sort={sentido === null ? "none" : sentido === "asc" ? "ascending" : "descending"}
    >
      {/*
        Un `<button>` y no un `onClick` en el `<th>`: una celda de encabezado no
        es un control, no recibe foco y no contesta a Enter ni a la barra
        espaciadora. Es la misma decisión que `paginado.tsx` §② tomó con «Ir».
      */}
      <button
        type="button"
        className="encabezado-ordenable__boton"
        onClick={() => onOrdenar(columna, proximoSentido(orden, columna))}
      >
        <span className="encabezado-ordenable__rotulo">{rotulo}</span>
        {/*
          ⛔ `aria-hidden`: las flechas son la forma VISUAL de la señal, y la
          señal accesible ya la da `aria-sort` en el `<th>`. Sin esto, el nombre
          del botón sería «Nombre ▲▼» y un lector de pantalla leería dos
          caracteres que no significan nada dichos en voz alta.

          ⚠️ Las clases se escriben como literales completos en cada rama del
          ternario —y no como `` `…__flecha${activa ? "--activa" : ""}` ``— porque
          `pruebas/r11-clases-sin-regla.test.ts` censa LITERALES: una clase armada
          por concatenación queda fuera del barrido y su regla podría faltar sin
          que nada se pusiera rojo. Su propio docblock lo declara como límite.
        */}
        <span className="encabezado-ordenable__flechas" aria-hidden="true">
          <span
            className={
              resaltaAscendente
                ? "encabezado-ordenable__flecha encabezado-ordenable__flecha--activa"
                : "encabezado-ordenable__flecha"
            }
          >
            ▲
          </span>
          <span
            className={
              resaltaDescendente
                ? "encabezado-ordenable__flecha encabezado-ordenable__flecha--activa"
                : "encabezado-ordenable__flecha"
            }
          >
            ▼
          </span>
        </span>
      </button>
    </th>
  )
}
